/**
 * The two rules behind `usePoll`, kept here so they can be tested without a DOM.
 *
 * Both were learned from bugs that look nothing like each other and are the same mistake: treating
 * "a request is in flight" and "the data on screen" as if they were about one subject.
 */

/**
 * Never overlap, never *lose* a request.
 *
 * The original rule was only the first half: a tick was dropped while another was in flight, which is
 * right for an expensive endpoint (a session's event feed is a full logcat dump over adb, and
 * overlapping dumps starve the input and video of the very phone being watched).
 *
 * Dropping is wrong when the tick is not one of many. `LatestBuilds` fetches **once** per app — no
 * interval — so this happened: a build finished on borsch and its refresh went out; the tester switched
 * to an app of another machine; that app's fetch arrived while the first was in flight and was dropped;
 * the first answer came back and was discarded as stale. Nothing was left to ask again, and the panel
 * showed borsch's APKs under a dimy-poliaka app until the page was reloaded.
 *
 * So a tick that arrives during another is **remembered** and runs when that one finishes. One bit is
 * enough: what matters is that the latest request happens, not how many were asked for meanwhile.
 */
export function ticker(run: () => Promise<void>): { tick: () => Promise<void>; busy: () => boolean } {
  let busy = false
  let queued = false

  const tick = async (): Promise<void> => {
    if (busy) {
      queued = true
      return
    }
    busy = true
    try {
      await run()
    } finally {
      busy = false
      if (queued) {
        queued = false
        await tick()
      }
    }
  }
  return { tick, busy: () => busy }
}

/** Are these the same subject? Identity per element — the deps are ids, flags and object references. */
export function sameSubject(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i])
}

/**
 * Data belongs to the subject it was fetched for.
 *
 * Held state plus changed deps used to mean the previous app's artifacts were rendered under the new
 * app's name — briefly in the good case, and this is the same failure the workspace already forbids for
 * a phone's video (no taps until a frame of the *current* device has been painted). A gap is honest;
 * one thing's data labelled as another's is not.
 */
export function forSubject<T>(
  snapshot: { key: readonly unknown[]; value: T | null },
  deps: readonly unknown[],
): T | null {
  return sameSubject(snapshot.key, deps) ? snapshot.value : null
}
